import { generalHelpers } from '@tweddle-se-team/common/helpers'
import axios from 'axios'
import { stringify } from 'qs'
import { getCookieValue } from '../auth'
import { apiUrlSsr } from '../env'

const isServer = generalHelpers.isServer()

// Helper function to extract IP-related headers from Next.js context
const getForwardHeaders = ctx => {
  const reqHeaders = ctx?.req?.headers || {}
  const forwardHeaders = {}

  // Explicitly whitelist headers to forward to backend for rate limiting and logging
  if (reqHeaders['x-forwarded-for']) {
    forwardHeaders['x-forwarded-for'] = reqHeaders['x-forwarded-for']
  }
  if (reqHeaders['x-real-ip']) {
    forwardHeaders['x-real-ip'] = reqHeaders['x-real-ip']
  }
  if (reqHeaders['user-agent']) {
    forwardHeaders['user-agent'] = reqHeaders['user-agent']
  }

  return forwardHeaders
}

// Helper to validate URL is for backend API (not external)
const validateBackendUrl = url => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    throw new Error(
      `apiWrapper is for backend API calls only. Got external URL: "${url}". ` +
        `Use externalFetcher from '@/utils/apiHelpers/externalFetcher' for external resources.`
    )
  }
}

// Unified axios instance creator - handles both authenticated and anonymous requests
const createAxiosInstance = ctx => {
  // Get JWT if available (only on server-side)
  const jwt = isServer ? getCookieValue(ctx, 'jwt') : null

  // Forward IP headers when on server (SSR context)
  const forwardHeaders = isServer ? getForwardHeaders(ctx) : {}

  // Determine base URL based on environment
  const baseURL = isServer ? apiUrlSsr : window.RUNTIME_CONFIG?.apiUrlClient

  if (!baseURL) {
    throw new Error('API URL not configured. Please check your environment configuration.')
  }

  // Create axios instance for backend API calls
  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      ...(jwt && { Authorization: `Bearer ${jwt}` }),
      ...forwardHeaders
    },
    withCredentials: true // Always send credentials to our backend
  })

  // Unwrap data from response
  instance.interceptors.response.use(res => res.data)

  return instance
}

// Export helper methods - for backend API calls ONLY
const apiWrapper = {
  get: async (url, query = {}, ctx) => {
    validateBackendUrl(url)
    const queryString = query ? `?${stringify(query)}` : ''
    return createAxiosInstance(ctx).get(`${url}${queryString}`)
  },

  post: async (url, data = {}, ctx) => {
    validateBackendUrl(url)
    return createAxiosInstance(ctx).post(url, data)
  },

  put: async (url, data = {}, ctx) => {
    validateBackendUrl(url)
    return createAxiosInstance(ctx).put(url, data)
  },

  patch: async (url, data = {}, ctx) => {
    validateBackendUrl(url)
    return createAxiosInstance(ctx).patch(url, data)
  },

  delete: async (url, data = {}, ctx) => {
    validateBackendUrl(url)
    return createAxiosInstance(ctx).delete(url, data)
  },

  /**
   * Upload a file (FormData) to a backend endpoint.
   * Uses a fresh axios instance without a forced Content-Type so that
   * the browser can set the correct multipart/form-data boundary automatically.
   */
  upload: async (url, formData) => {
    validateBackendUrl(url)
    const baseURL = window.RUNTIME_CONFIG?.apiUrlClient
    if (!baseURL) {
      throw new Error('API URL not configured. Please check your environment configuration.')
    }
    const instance = axios.create({
      baseURL,
      withCredentials: true
      // Content-Type is intentionally omitted here; axios/FormData sets it with the correct multipart boundary
    })
    instance.interceptors.response.use(res => res.data)
    return instance.post(url, formData)
  },

  getHeaders: ctx => {
    // Keep for backward compatibility, but now just returns auth header
    // The IP headers are automatically forwarded by the instance creator
    if (!ctx) return {}
    const jwt = getCookieValue(ctx, 'jwt')
    return {
      headers: {
        ...(jwt && { Authorization: `Bearer ${jwt}` })
      }
    }
  }
}

export default apiWrapper
